"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ResourcePageBookingConfigurator = void 0;
const react_1 = __importStar(require("react"));
const Col_1 = __importDefault(require("react-bootstrap/Col"));
const Container_1 = __importDefault(require("react-bootstrap/Container"));
const Row_1 = __importDefault(require("react-bootstrap/Row"));
const react_router_dom_1 = require("react-router-dom");
const configContext_1 = require("../../context/configContext");
const environment_1 = require("../../environment");
const BookingItem_1 = require("../../models/BookingItem");
const CustomerBooking_1 = require("../../models/CustomerBooking");
const Breadcrumbs_1 = require("../breadcrumbs/Breadcrumbs");
const StepAddons_1 = require("./StepAddons");
const StepContact_1 = require("./StepContact");
const StepDateAndTime_1 = require("./StepDateAndTime");
const StepDetails_1 = require("./StepDetails");
const StepThanks_1 = require("./StepThanks");
const Summary_1 = require("./summary/Summary");
/**
 * The configurator for customers to create a new booking.
 */
const ResourcePageBookingConfigurator = (props) => {
    const { resourceId } = (0, react_router_dom_1.useParams)();
    if (!resourceId) {
        throw new Error("No resourceId provided in url parameters.");
    }
    const navigate = (0, react_router_dom_1.useNavigate)();
    const [currentStep, setCurrentStep] = (0, react_1.useState)(0);
    const [discountCode, setDiscountCode] = (0, react_1.useState)("");
    const config = (0, configContext_1.useConfig)();
    /** Get the current resource config. Memo will only recompute if the resourceId changes. */
    const configResource = (0, react_1.useMemo)(() => {
        const resource = config.getVisibleResourceById(resourceId);
        if (resource !== undefined) {
            return resource;
        }
        else {
            throw new Error(`Resource not found: id=${resourceId}`);
        }
    }, [resourceId]);
    const [booking, setBooking] = (0, react_1.useState)(() => createInitialBooking(resourceId));
    function createInitialBooking(resourceId) {
        const initialBooking = new CustomerBooking_1.CustomerBooking(resourceId, config);
        // Add addon items with min. quantity > 0.
        configResource.addonItems.forEach((configAddon) => {
            if (configAddon.minQuantity > 0) {
                const addonItem = new BookingItem_1.BookingItem(config, configAddon);
                initialBooking.addonItems.push(addonItem);
            }
        });
        initialBooking.update();
        return initialBooking;
    }
    function handleResourceChange(newResourceId) {
        navigate(`/resources/${config.tenantId}/${newResourceId}/`);
        // Reset the booking and only keep the start and end time.
        const newBooking = createInitialBooking(newResourceId);
        newBooking.start = booking.start;
        newBooking.end = booking.end;
        setBooking(newBooking);
    }
    function handleBookingChange(newBooking) {
        setBooking((prevBooking) => {
            const mergedBooking = CustomerBooking_1.CustomerBooking.fromPartial(newBooking, prevBooking, config);
            mergedBooking.update();
            mergedBooking.sortAddons();
            mergedBooking.useDiscountCode(discountCode);
            return mergedBooking;
        });
    }
    function handleDiscountCodeChange(newDiscountCode) {
        setDiscountCode(newDiscountCode);
        // Discount code is not saved in booking but we need to update the
        // booking with the new code.
        setBooking((prevBooking) => {
            const mergedBooking = CustomerBooking_1.CustomerBooking.fromPartial({}, prevBooking, config);
            mergedBooking.useDiscountCode(newDiscountCode);
            return mergedBooking;
        });
    }
    /** Changes to the previous step. */
    function handlePrevious() {
        setCurrentStep((prevCurrentStep) => prevCurrentStep - 1);
    }
    /** Changes to the next step. */
    function handleNext() {
        setCurrentStep((prevCurrentStep) => prevCurrentStep + 1);
    }
    /** Submits the booking to the server. */
    async function handleSubmit() {
        try {
            const response = await fetch(`${(0, environment_1.getEnv)().baseUrl}/api/bookings`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(booking),
            });
            const responseJson = await response.json();
            // Server adds `id` and `reference` to the booking.
            handleBookingChange({
                id: responseJson.id,
                reference: responseJson.reference,
            });
            // Go to next step with updated booking.
            handleNext();
        }
        catch (error) {
            console.error(error);
            throw error;
        }
    }
    /** Creates all the steps of the configurator. */
    function createSteps() {
        const steps = [];
        // Add the "Zeitraum" step.
        const dateAndTimeType = "Zeitraum";
        steps.push({
            type: dateAndTimeType,
            showBreadcrumb: true,
            element: (react_1.default.createElement(StepDateAndTime_1.StepDateAndTime, { key: dateAndTimeType, booking: booking, onBookingChange: handleBookingChange, onResourceChange: handleResourceChange, onNext: handleNext })),
        });
        // Add the "Addon" steps (grouped by type).
        const addonsByType = configResource.getAddonsGroupedByType();
        for (let type in addonsByType) {
            const addons = addonsByType[type];
            steps.push({
                type,
                showBreadcrumb: true,
                element: (react_1.default.createElement(StepAddons_1.StepAddons, { key: type, type: type, configAddonItems: addons, booking: booking, onBookingChange: handleBookingChange, onPrevious: handlePrevious, onNext: handleNext })),
            });
        }
        // Add the "Kontakt" step.
        const contactType = "Kontakt";
        steps.push({
            type: contactType,
            showBreadcrumb: true,
            element: (react_1.default.createElement(StepContact_1.StepContact, { key: contactType, type: contactType, booking: booking, onBookingChange: handleBookingChange, onPrevious: handlePrevious, onNext: handleNext })),
        });
        // Add the "Display" step.
        const detailsType = "Details";
        steps.push({
            type: detailsType,
            showBreadcrumb: true,
            element: (react_1.default.createElement(StepDetails_1.StepDetails, { key: detailsType, type: detailsType, booking: booking, onBookingChange: handleBookingChange, discountCode: discountCode, onDiscountCodeChange: handleDiscountCodeChange, onPrevious: handlePrevious, onSubmit: handleSubmit })),
        });
        // Add the "Thanks" step.
        const thanksType = "Danke";
        steps.push({
            type: thanksType,
            showBreadcrumb: false,
            element: (react_1.default.createElement(StepThanks_1.StepThanks, { key: thanksType, type: thanksType, booking: booking })),
        });
        return steps;
    }
    const steps = createSteps();
    return (react_1.default.createElement(Container_1.default, { fluid: true, className: "main" },
        react_1.default.createElement(Row_1.default, null,
            react_1.default.createElement(Col_1.default, { md: { span: 4, order: 2 }, className: "summary sticky-top" },
                react_1.default.createElement("div", { className: "d-none d-md-block" },
                    react_1.default.createElement("h2", { className: "display-5" }, "\u00DCbersicht"),
                    react_1.default.createElement("hr", null)),
                react_1.default.createElement(Summary_1.Summary, { booking: booking })),
            react_1.default.createElement(Col_1.default, { md: { span: 8, order: 1 }, className: "steps" },
                react_1.default.createElement("img", { className: "float-right", style: { maxHeight: "50px", maxWidth: "200px" }, src: config.getImageUrl(config.logo) }),
                react_1.default.createElement("div", null,
                    react_1.default.createElement("h1", { className: "display-5" },
                        "\u00AB",
                        booking.resourceItem.title,
                        "\u00BB buchen")),
                react_1.default.createElement(Breadcrumbs_1.Breadcrumbs, { className: "d-none d-sm-block", steps: steps, currentStep: currentStep }),
                steps[currentStep].element))));
};
exports.ResourcePageBookingConfigurator = ResourcePageBookingConfigurator;
